import type {
	App,
	Directive,
} from 'vue';

const HANDLERS_PROPERTY = '__v-caret-restore';

/**
 * User supplies a "normalizer": any function that takes the raw textarea
 * value and returns either a *synchronous* replacement string **or**
 * a Promise that resolves to the replacement string.
 */
export type Normalizer = (value: string) => string | Promise<string>;
/**
 * Extend the native element so we can stash a reference to the
 * event-handler for clean-up in `unmounted`.
 */
type HTMLTextAreaElementWithVCaretRestore = (
	HTMLTextAreaElement
	& {
		[HANDLERS_PROPERTY]?: (event: Event) => void;
	}
);

/**
 * Directive to restore the caret position in textareas.
 *
 * Purpose:
 *
 * - When you programmatically change the `<textarea>` value (e.g.,
 * 	stripping soft-hyphens, inserting soft-hyphens, normalizing whitespace),
 * 	the browser resets the caret. This directive restores the caret to
 * 	the logical position after every *input* event.
 *
 * How it works (high-level):
 *
 * 		1. Snapshot selection
 * 		2. Run user normaliser
 * 		3. Patch value if needed
 * 		4. Compute difference
 * 		5. Restore caret
 * 		6. Emit synthetic `input` for v-model
 */
export const directive: Directive<HTMLTextAreaElementWithVCaretRestore, Normalizer> = {
	mounted(element, binding) {
		if (!(element instanceof HTMLTextAreaElement)) {
			console.warn('v-caret-restore should be used on a <textarea>.');
			return;
		}

		/**
		 * Main input-phase handler
		 */
		const onInput = async (event: Event) => {
			const normalize = binding.value;

			if (typeof normalize !== 'function') {
				console.warn('v-caret-restore: Binding value must be a function');
				return;
			}

			/**
			 * Snapshot selection **before** we mutate the value
			 */
			const startBefore = (
				element.selectionStart
				?? 0
			);
			const endBefore = (
				element.selectionEnd
				?? 0
			);
			let newText: string | Promise<string> = '';

			/**
			 * Let user normalize the text (sync or async)
			 */
			newText = normalize(element.value);

			if (newText instanceof Promise) {
				/**
				 * If the normalizer returns a promise, we await it.
				 */
				newText = await newText;
			}

			/**
			 * Bail early if nothing changed
			 */
			if (newText === element.value) {
				return;
			}

			/**
			 * Compute code-unit delta that occurred *before* the caret
			 */
			const difference = newText.length - element.value.length;
			const startAfter = Math.max(
				0,
				startBefore + difference,
			);
			const endAfter = Math.max(
				0,
				endBefore + difference,
			);
			/**
			 * Since we are going to re-emit the event, we should stop
			 * immediate propagation to prevent Vue's v-model, or other
			 * event listeners, from interfering with our logic.
			 */
			event.stopImmediatePropagation();

			/**
			 * Update the value manually so Vue's v-model doesn't clobber caret
			 */
			element.value = newText;
			element.setSelectionRange(
				startAfter,
				endAfter,
			);

			/**
			 * Dispatch synthetic `input` so v-model sees the new value
			 */
			element.dispatchEvent(new Event(
				'input',
				{
					bubbles: true,
				},
			));
		};

		/**
		 * Store dispose function and attach listener (capture phase => runs first)
		 */
		element[HANDLERS_PROPERTY] = onInput;
		element.addEventListener(
			'input',
			onInput,
			{
				capture: true, // capture phase to ensure it runs before Vue's v-model
			},
		);
	},
	unmounted(element) {
		const handler = element[HANDLERS_PROPERTY];

		if (handler) {
			/**
			 * Clean-up: detach the handler when the element is removed
			 */
			element.removeEventListener(
				'input',
				handler,
			);
		}
	},
};

/**
 * Vue plugin to install the v-caret-restore directive using `app.use(...)`.
 * This allows the directive to be used in any Vue component.
 */
const plugin = {
	install(app: App): void {
		app.directive(
			'caret-restore',
			directive,
		);
	},
	directive,
};

export default plugin;
